import { LottoDraw, PredictionSet, PredictionBatch, LottoStats, NumberFrequency, PairFrequency, PredictionStrategy } from "../types";

const API_ENDPOINT = "/api/lotto";
const CACHE_KEYS = {
  latest: "tt-lotto-latest-v2",
  history: "tt-lotto-history-v2",
};

interface CachedValue<T> {
  savedAt: number;
  value: T;
}

const readCache = <T>(key: string, maxAgeMs: number): T | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedValue<T>;
    if (!parsed?.savedAt || Date.now() - parsed.savedAt > maxAgeMs) return null;
    return parsed.value;
  } catch {
    return null;
  }
};

const writeCache = <T>(key: string, value: T) => {
  try {
    localStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), value }));
  } catch {
    // Storage can be unavailable in private/restricted browser modes.
  }
};

const getJson = async <T>(action: "latest" | "history"): Promise<T> => {
  const response = await fetch(`${API_ENDPOINT}?action=${action}`, {
    headers: { Accept: "application/json" },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = typeof payload?.error === "string" ? payload.error : `Request failed (${response.status})`;
    throw new Error(message);
  }
  return payload as T;
};

const normalizeDraw = (draw: LottoDraw): LottoDraw | null => {
  const numbers = Array.isArray(draw?.numbers)
    ? [...new Set(draw.numbers.map(Number).filter(n => Number.isInteger(n) && n >= 1 && n <= 35))].slice(0, 5)
    : [];
  const powerball = Number(draw?.powerball);
  if (numbers.length !== 5 || !Number.isInteger(powerball) || powerball < 1 || powerball > 10) return null;

  return {
    date: String(draw.date || "Unknown Date"),
    drawNumber: draw.drawNumber == null ? undefined : Number(draw.drawNumber),
    numbers: numbers.sort((a, b) => a - b),
    powerball,
    multiplier: /^(?:3|4|5|10|20)X$/i.test(String(draw.multiplier || "")) ? String(draw.multiplier).toUpperCase() : null,
    jackpot: String(draw.jackpot || "TBA"),
    sourceUrl: draw.sourceUrl,
    sourceTitle: draw.sourceTitle,
    jackpotUpdatedAt: draw.jackpotUpdatedAt,
    nextEstimatedJackpot: draw.nextEstimatedJackpot ?? null,
    officialVerifiedAt: draw.officialVerifiedAt ?? null,
  };
};

export const fetchLatestLottoResults = async (): Promise<LottoDraw> => {
  try {
    const draw = normalizeDraw(await getJson<LottoDraw>("latest"));
    if (!draw) throw new Error("The latest draw returned invalid number data.");
    writeCache(CACHE_KEYS.latest, draw);
    return draw;
  } catch (error) {
    const cached = readCache<LottoDraw>(CACHE_KEYS.latest, 7 * 24 * 60 * 60 * 1000);
    if (cached) return cached;
    throw error;
  }
};

export const fetchHistory = async (): Promise<LottoDraw[]> => {
  try {
    const rawHistory = await getJson<LottoDraw[]>("history");
    const history = Array.isArray(rawHistory)
      ? rawHistory.map(normalizeDraw).filter((draw): draw is LottoDraw => Boolean(draw))
      : [];
    if (history.length === 0) throw new Error("No valid historical draws were returned.");
    history.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    writeCache(CACHE_KEYS.history, history);
    return history;
  } catch (error) {
    const cached = readCache<LottoDraw[]>(CACHE_KEYS.history, 30 * 24 * 60 * 60 * 1000);
    if (cached?.length) return cached;
    throw error;
  }
};



export const refreshCurrentNLCB = async (): Promise<{ ok: boolean; latestDrawNumber?: number | null; nextEstimatedJackpot?: string | null; officialVerifiedAt?: string | null }> => {
  const response = await fetch(`${API_ENDPOINT}?action=refresh-current`, {
    method: "POST",
    headers: { Accept: "application/json" },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = typeof payload?.error === "string" ? payload.error : `Current NLCB refresh failed (${response.status})`;
    throw new Error(message);
  }
  // Force subsequent reads to use the freshly verified server-side data.
  try {
    localStorage.removeItem(CACHE_KEYS.latest);
    localStorage.removeItem(CACHE_KEYS.history);
  } catch {}
  return payload;
};

export const syncNLCBHistory = async (): Promise<{ cachedDraws: number; targetDraws: number; output?: string }> => {
  const response = await fetch(`${API_ENDPOINT}?action=sync`, {
    method: "POST",
    headers: { Accept: "application/json" },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = typeof payload?.error === "string" ? payload.error : `Sync failed (${response.status})`;
    throw new Error(message);
  }
  return payload;
};

export const fetchLottoStats = async (history: LottoDraw[] = []): Promise<LottoStats> => {
  const allCounts = Array.from({ length: 35 }, (_, i) => ({ number: i + 1, frequency: 0 }));
  const pairCounts = new Map<string, number>();

  const orderedHistory = [...history].sort((a, b) => {
    if (a.drawNumber && b.drawNumber) return b.drawNumber - a.drawNumber;
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  for (const draw of orderedHistory) {
    const nums = [...new Set(draw.numbers)].filter(n => n >= 1 && n <= 35).sort((a, b) => a - b);
    for (const n of nums) allCounts[n - 1].frequency += 1;
    for (let i = 0; i < nums.length; i += 1) {
      for (let j = i + 1; j < nums.length; j += 1) {
        const key = `${nums[i]}-${nums[j]}`;
        pairCounts.set(key, (pairCounts.get(key) || 0) + 1);
      }
    }
  }

  if (orderedHistory.length === 0) {
    return { hotNumbers: [], coldNumbers: [], frequencyData: [], frequentPairs: [], frequencyWindow: 0 };
  }

  const descending = [...allCounts].sort((a, b) => b.frequency - a.frequency || a.number - b.number);
  const ascending = [...allCounts].sort((a, b) => a.frequency - b.frequency || a.number - b.number);
  const frequentPairs: PairFrequency[] = [...pairCounts.entries()]
    .map(([key, frequency]) => {
      const [a, b] = key.split("-").map(Number);
      return { numbers: [a, b] as [number, number], frequency };
    })
    .sort((a, b) => b.frequency - a.frequency)
    .slice(0, 3);

  const recent = orderedHistory.slice(0, 50);
  const recentCounts = Array.from({ length: 35 }, (_, i) => ({ number: i + 1, frequency: 0 }));
  for (const draw of recent) {
    for (const n of new Set(draw.numbers)) {
      if (n >= 1 && n <= 35) recentCounts[n - 1].frequency += 1;
    }
  }
  const recentTop10 = recentCounts
    .sort((a, b) => b.frequency - a.frequency || a.number - b.number)
    .slice(0, 10);

  return {
    hotNumbers: descending.slice(0, 5).map(x => x.number),
    coldNumbers: ascending.slice(0, 5).map(x => x.number),
    frequencyData: recentTop10 as NumberFrequency[],
    frequentPairs,
    frequencyWindow: recent.length,
  };
};

export const savePredictionBatch = async (payload: {
  targetDrawNumber: number;
  targetDrawDate: string;
  predictions: PredictionSet[];
}): Promise<PredictionBatch> => {
  const response = await fetch(`${API_ENDPOINT}?action=predictions`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data?.error || `Unable to save predictions (${response.status})`);
  return data as PredictionBatch;
};

export const fetchLatestPredictionBatch = async (): Promise<PredictionBatch | null> => {
  const response = await fetch(`${API_ENDPOINT}?action=predictions`, { headers: { Accept: "application/json" } });
  const data = await response.json().catch(() => null);
  if (!response.ok) throw new Error(data?.error || `Unable to load predictions (${response.status})`);
  return data as PredictionBatch | null;
};

export const fetchPredictionBatches = async (): Promise<PredictionBatch[]> => {
  const response = await fetch(`${API_ENDPOINT}?action=predictions-list`, { headers: { Accept: "application/json" } });
  const data = await response.json().catch(() => []);
  if (!response.ok) throw new Error(data?.error || `Unable to load generator history (${response.status})`);
  return Array.isArray(data) ? data as PredictionBatch[] : [];
};

export interface LottoStatus {
  cachedDraws?: number;
  officialVerifiedAt?: string | null;
  lastAutoSyncAt?: string | null;
  nextAutoSyncAt?: string | null;
  autoSyncEnabled?: boolean;
}

export const fetchLottoStatus = async (): Promise<LottoStatus> => {
  const response = await fetch(`${API_ENDPOINT}?action=status`, { headers: { Accept: "application/json" } });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data?.error || `Unable to load Lotto status (${response.status})`);
  return data as LottoStatus;
};

const randomUnit = () => {
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const values = new Uint32Array(1);
    crypto.getRandomValues(values);
    return values[0] / 0x100000000;
  }
  return Math.random();
};

const randomInt = (min: number, max: number) => Math.floor(randomUnit() * (max - min + 1)) + min;

const weightedPick = (weights: number[], excluded = new Set<number>()) => {
  const candidates = weights
    .map((weight, index) => ({ number: index + 1, weight: Math.max(0.001, weight) }))
    .filter(item => !excluded.has(item.number));
  const total = candidates.reduce((sum, item) => sum + item.weight, 0);
  let target = randomUnit() * total;
  for (const item of candidates) {
    target -= item.weight;
    if (target <= 0) return item.number;
  }
  return candidates[candidates.length - 1]?.number ?? randomInt(1, 35);
};

const lineIsBalanced = (numbers: number[]) => {
  if (numbers.length !== 5 || new Set(numbers).size !== 5) return false;
  if (numbers.some(n => n < 1 || n > 35)) return false;
  const sum = numbers.reduce((a, b) => a + b, 0);
  if (sum < 65 || sum > 115) return false;
  const low = numbers.filter(n => n <= 17).length;
  const odd = numbers.filter(n => n % 2 === 1).length;
  return (low === 2 || low === 3) && (odd === 2 || odd === 3);
};

const consecutiveGroups = (numbers: number[]) => {
  const sorted = [...numbers].sort((a, b) => a - b);
  let pairs = 0;
  let hasTriplet = false;
  for (let i = 1; i < sorted.length; i += 1) {
    if (sorted[i] === sorted[i - 1] + 1) pairs += 1;
    if (i >= 2 && sorted[i] === sorted[i - 1] + 1 && sorted[i - 1] === sorted[i - 2] + 1) hasTriplet = true;
  }
  return { pairs, hasTriplet };
};

const weightedLine = (weights: number[], seed: number[] = [], requireSingleConsecutivePair = false) => {
  for (let attempt = 0; attempt < 1800; attempt += 1) {
    const selected = new Set(seed.filter(n => n >= 1 && n <= 35));
    while (selected.size < 5) selected.add(weightedPick(weights, selected));
    const numbers = [...selected].slice(0, 5).sort((a, b) => a - b);
    if (!lineIsBalanced(numbers)) continue;
    if (requireSingleConsecutivePair) {
      const consecutive = consecutiveGroups(numbers);
      if (consecutive.pairs !== 1 || consecutive.hasTriplet) continue;
    }
    return numbers;
  }

  // Deterministic safety fallback that always satisfies the core Lotto Plus filters.
  const fallbacks = [
    [4, 11, 18, 25, 32],
    [5, 12, 19, 26, 33],
    [6, 13, 20, 27, 34],
    [7, 14, 21, 28, 35],
  ];
  if (requireSingleConsecutivePair) return [8, 15, 22, 23, 31];
  return fallbacks[randomInt(0, fallbacks.length - 1)];
};

const drawRecencyGaps = (history: LottoDraw[]) => {
  const gaps = Array(35).fill(history.length + 1) as number[];
  history.forEach((draw, index) => {
    draw.numbers.forEach(n => {
      if (n >= 1 && n <= 35 && gaps[n - 1] === history.length + 1) gaps[n - 1] = index;
    });
  });
  return gaps;
};

const overduePowerballPool = (history: LottoDraw[]) => {
  const recent = history.slice(0, 10);
  const lastSeen = Array(10).fill(999) as number[];
  recent.forEach((draw, index) => {
    if (draw.powerball >= 1 && draw.powerball <= 10 && lastSeen[draw.powerball - 1] === 999) lastSeen[draw.powerball - 1] = index;
  });
  return lastSeen
    .map((gap, index) => ({ number: index + 1, gap }))
    .sort((a, b) => b.gap - a.gap || a.number - b.number)
    .slice(0, 3)
    .map(item => item.number);
};

const commonConsecutivePairs = (history: LottoDraw[]) => {
  const counts = new Map<string, number>();
  for (const draw of history) {
    const sorted = [...new Set(draw.numbers)].sort((a, b) => a - b);
    for (let i = 1; i < sorted.length; i += 1) {
      if (sorted[i] === sorted[i - 1] + 1) {
        const key = `${sorted[i - 1]}-${sorted[i]}`;
        counts.set(key, (counts.get(key) || 0) + 1);
      }
    }
  }
  return [...counts.entries()]
    .map(([key, frequency]) => ({ numbers: key.split('-').map(Number) as [number, number], frequency }))
    .sort((a, b) => b.frequency - a.frequency || a.numbers[0] - b.numbers[0]);
};

const buildStrategyLine = (
  strategy: PredictionStrategy,
  hotWeights: number[],
  overdueWeights: number[],
  neutralWeights: number[],
  consecutivePairs: Array<{ numbers: [number, number]; frequency: number }>,
  lineIndex: number,
) => {
  if (strategy === 'Historical Hot Mix') {
    return weightedLine(hotWeights);
  }
  if (strategy === 'Overdue Balancer') {
    return weightedLine(overdueWeights);
  }
  if (strategy === 'Range Balanced') {
    const ranges: Array<[number, number]> = [[1, 7], [8, 14], [15, 21], [22, 28], [29, 35]];
    for (let attempt = 0; attempt < 500; attempt += 1) {
      const seed = ranges.map(([min, max]) => randomInt(min, max));
      const unique = [...new Set(seed)].sort((a, b) => a - b);
      if (lineIsBalanced(unique)) return unique;
    }
    return weightedLine(neutralWeights);
  }

  if (strategy === 'Common Pair Mix') {
    const pair = consecutivePairs[lineIndex % Math.max(1, consecutivePairs.length)]?.numbers;
    if (pair) return weightedLine(hotWeights, pair, true);
    const start = randomInt(1, 34);
    return weightedLine(hotWeights, [start, start + 1], true);
  }

  return weightedLine(neutralWeights);
};

const buildAdaptiveEnsembleLine = (history: LottoDraw[], baseWeights: number[], consecutivePairs: Array<{ numbers: [number, number]; frequency: number }>) => {
  const ordered = [...history].sort((a, b) => (b.drawNumber || 0) - (a.drawNumber || 0));
  const decay = Array(35).fill(0) as number[];
  const gaps = drawRecencyGaps(ordered);
  ordered.forEach((draw, index) => {
    const w = Math.pow(0.985, index);
    draw.numbers.forEach(n => { if (n >= 1 && n <= 35) decay[n - 1] += w; });
  });
  const maxDecay = Math.max(1, ...decay);
  const maxGap = Math.max(1, ...gaps);
  const pairMap = new Map<string, number>();
  consecutivePairs.forEach(item => pairMap.set(`${item.numbers[0]}-${item.numbers[1]}`, item.frequency));
  const maxPair = Math.max(1, ...consecutivePairs.map(item => item.frequency));
  const ensembleWeights = baseWeights.map((w, i) => {
    const recencyFrequency = decay[i] / maxDecay;
    const overdue = Math.min(gaps[i], 20) / Math.min(maxGap, 20);
    return Math.max(0.01, w * 0.35 + recencyFrequency * 5.2 + overdue * 1.8);
  });

  let best = weightedLine(ensembleWeights);
  let bestScore = -Infinity;
  for (let attempt = 0; attempt < 3500; attempt += 1) {
    const candidate = weightedLine(ensembleWeights);
    const individual = candidate.reduce((sum, n) => sum + ensembleWeights[n - 1], 0);
    let pairScore = 0;
    for (let i = 0; i < candidate.length; i += 1) {
      for (let j = i + 1; j < candidate.length; j += 1) {
        const key = `${candidate[i]}-${candidate[j]}`;
        const reverse = `${candidate[j]}-${candidate[i]}`;
        pairScore += (pairMap.get(key) || pairMap.get(reverse) || 0) / maxPair;
      }
    }
    const sum = candidate.reduce((a, b) => a + b, 0);
    const centerBonus = 1 - Math.min(1, Math.abs(sum - 90) / 25);
    const score = individual + pairScore * 1.6 + centerBonus * 2.2;
    if (score > bestScore) { bestScore = score; best = candidate; }
  }
  return best;
};

const chooseFive = (pool: number[]) => {
  const combos: number[][] = [];
  for (let a = 0; a < pool.length - 4; a += 1)
    for (let b = a + 1; b < pool.length - 3; b += 1)
      for (let c = b + 1; c < pool.length - 2; c += 1)
        for (let d = c + 1; d < pool.length - 1; d += 1)
          for (let e = d + 1; e < pool.length; e += 1)
            combos.push([pool[a], pool[b], pool[c], pool[d], pool[e]]);
  return combos;
};

export const generateSmartPredictions = async (
  _latestDraw: LottoDraw | null,
  stats: LottoStats | null,
  history: LottoDraw[] = [],
  selectedStrategy: PredictionStrategy = 'All Strategies',
  wheelMode = false,
): Promise<PredictionSet[]> => {
  const orderedHistory = [...history].sort((a, b) => (b.drawNumber || 0) - (a.drawNumber || 0));
  const frequencies = Array(35).fill(1) as number[];
  for (const draw of orderedHistory) {
    for (const n of draw.numbers) if (n >= 1 && n <= 35) frequencies[n - 1] += 1;
  }

  const gaps = drawRecencyGaps(orderedHistory);
  const maxFreq = Math.max(...frequencies);
  const hotWeights = frequencies.map((f, i) => f * 1.8 + Math.sqrt(gaps[i] + 1) * 0.25 + 1);
  const overdueWeights = frequencies.map((f, i) => (maxFreq - f + 2) * 0.8 + (gaps[i] + 1) * 2.2);
  const neutralWeights = frequencies.map(f => Math.sqrt(f + 1));
  const consecutivePairs = commonConsecutivePairs(orderedHistory);
  const pbPool = overduePowerballPool(orderedHistory);

  const strategyOrder: PredictionStrategy[] = selectedStrategy === 'All Strategies'
    ? ['Historical Hot Mix', 'Overdue Balancer', 'Range Balanced', 'Common Pair Mix', 'Adaptive Ensemble']
    : [selectedStrategy, selectedStrategy, selectedStrategy, selectedStrategy, selectedStrategy];

  if (wheelMode) {
    // Build a definitive 7-number pool from hot/overdue information, then produce the 21 possible 5-number lines.
    const combinedWeights = hotWeights.map((weight, index) => weight + overdueWeights[index] * 0.45);
    let pool: number[] = [];
    for (let attempt = 0; attempt < 600 && pool.length !== 7; attempt += 1) {
      const selected = new Set<number>();
      while (selected.size < 7) selected.add(weightedPick(combinedWeights, selected));
      const candidate = [...selected].sort((a, b) => a - b);
      const validCombos = chooseFive(candidate).filter(lineIsBalanced);
      if (validCombos.length >= 8) pool = candidate;
    }
    if (pool.length !== 7) pool = [4, 9, 14, 19, 24, 29, 34];

    const combos = chooseFive(pool);
    return combos.map((numbers, index) => ({
      id: index + 1,
      strategy: `7-Number Wheel • ${selectedStrategy === 'All Strategies' ? 'Blended' : selectedStrategy}`,
      numbers,
      powerball: pbPool[index % pbPool.length] || randomInt(1, 10),
      confidence: lineIsBalanced(numbers) ? 86 : 74,
    }));
  }

  return strategyOrder.map((strategy, index) => ({
    id: index + 1,
    strategy,
    numbers: strategy === 'Adaptive Ensemble'
      ? buildAdaptiveEnsembleLine(orderedHistory, neutralWeights, consecutivePairs)
      : buildStrategyLine(strategy, hotWeights, overdueWeights, neutralWeights, consecutivePairs, index),
    powerball: pbPool[index % pbPool.length] || randomInt(1, 10),
    confidence: orderedHistory.length >= 50
      ? (strategy === 'Historical Hot Mix' ? 88 : strategy === 'Overdue Balancer' ? 85 : strategy === 'Common Pair Mix' ? 86 : strategy === 'Adaptive Ensemble' ? 89 : 84)
      : 72,
  }));
};
